import * as React from "react";

import { initials } from "../../lib/initials";
import { AgentBadge } from "./agent-badge";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Picker, type PickerOption, type PickerVariant } from "./picker";

/**
 * People are workspace records, so picking one is always Job B
 * (`docs/picker-taxonomy.md` §1.1) — a three-person workspace becomes a
 * three-hundred-person workspace and the mechanism must not change underneath
 * it.
 */

export interface MemberOption {
  id: string;
  name: string;
  avatar?: string | null;
  subtitle?: string;
  /**
   * An agent principal rather than a person. Badged so nobody mistakes it for
   * a colleague — it stays in the list, because assigning work to it is a
   * feature, not a mistake to prevent.
   */
  isAgent?: boolean;
}

export interface MemberComboboxProps {
  value?: string | string[];
  multiple?: boolean;
  members: MemberOption[];
  onChange: (value: string | string[] | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  /* ── Additive pass-throughs (§6.8) ─────────────────────────────────────
     Every one of these already existed on the underlying picker and was
     simply unreachable from here — which is why `CreateTaskDialog` can
     debounce-search projects but not members. Defaults are unchanged, so no
     existing call site behaves differently. */
  /** Trigger geometry. Must match the `Select`s it sits beside. */
  variant?: PickerVariant;
  clearable?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** Server-side search. */
  onSearch?: (query: string) => void;
  groupBy?: (option: PickerOption) => string;
  maxHeight?: number;
  /** Drop the trailing caret — for a chip that carries a leading glyph instead. */
  chevron?: boolean;
  /** Leading glyph in the trigger. See `PickerProps.icon`. */
  icon?: React.ReactNode;
  align?: "start" | "end";
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

interface MemberPickerOption extends PickerOption {
  avatar?: string | null;
  subtitle?: string;
  isAgent?: boolean;
}

/** Faces shown before the trigger switches to a `+N` remainder. */
const MAX_STACK = 4;

const MemberCombobox = React.forwardRef<HTMLButtonElement, MemberComboboxProps>(
  function MemberCombobox(
    {
      value,
      multiple,
      members,
      onChange,
      placeholder = "Select member...",
      searchPlaceholder = "Search members...",
      emptyMessage = "No members found",
      className,
      ...rest
    },
    ref,
  ) {
    const options = React.useMemo<MemberPickerOption[]>(
      () =>
        members.map((m) => ({
          value: m.id,
          label: m.name,
          avatar: m.avatar ?? undefined,
          subtitle: m.subtitle,
          isAgent: m.isAgent,
        })),
      [members],
    );

    /*
      A multi-select member trigger reading "4 selected" names a count but not
      the people, so the old composer had to repeat the names as a chip row
      underneath — the same fact rendered twice. An overlapping stack says who
      in the width the count was already taking.

      This runs for single-select too. `Picker`'s own single-value branch
      renders `option.icon` + label, and a member option carries an `avatar`,
      not an `icon` — so deferring to it dropped the face and left a lead or an
      assignee as a bare name, indistinguishable from a text property. A person
      is shown as face + name everywhere else in the app; the picker trigger is
      not the one place that gets to differ.
    */
    const renderStack = React.useCallback(
      (selected: PickerOption[], selectedCount: number) => {
        /*
          One member renders as avatar + name — more legible than a stack of
          one, and the shape every other single-person surface uses. This
          branch cannot defer to `Picker`'s own single-value rendering by
          returning `null`: supplying `renderValue` at all takes that branch
          out of play, and a `null` from here is read as "no value" and falls
          through to the placeholder.
        */
        if (selectedCount === 1) {
          const only = selected[0];
          if (!only) return null;
          const avatar = (only as MemberPickerOption).avatar ?? undefined;
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              <Avatar size="xs" className="shrink-0">
                {avatar && <AvatarImage src={avatar} />}
                <AvatarFallback className="bg-hover-strong text-xs font-medium text-text-secondary">
                  {initials(only.label)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{only.label}</span>
            </span>
          );
        }
        if (selectedCount < 1) return null;
        // Labels resolve lazily on a server-searched picker, so the overflow
        // is counted off `selectedCount` — the only number that is always true.
        const shown = selected.slice(0, MAX_STACK);
        const overflow = selectedCount - shown.length;
        return (
          <span className="flex min-w-0 items-center gap-1">
            {/*
              A 6px overlap on a 16px avatar leaves ~6px of face once the 2px
              separator ring is taken off, and four of them read as one smudge.
              4px keeps the stack compact while each face stays identifiable.
            */}
            <span className="flex shrink-0 items-center -space-x-1">
              {shown.map((option) => {
                const avatar = (option as MemberPickerOption).avatar ?? undefined;
                return (
                  <Avatar
                    key={option.value}
                    size="xs"
                    // The ring punches each avatar out of the one behind it;
                    // without it an overlapped stack reads as one smudge.
                    className="shrink-0 ring-2 ring-panel"
                  >
                    {avatar && <AvatarImage src={avatar} />}
                    <AvatarFallback className="bg-hover-strong text-xs font-medium text-text-secondary">
                      {initials(option.label)}
                    </AvatarFallback>
                  </Avatar>
                );
              })}
            </span>
            {overflow > 0 && (
              <span className="shrink-0 text-xs text-text-tertiary">
                +{overflow}
              </span>
            )}
          </span>
        );
      },
      [],
    );

    const renderOption = React.useCallback((option: PickerOption) => {
      const member = option as MemberPickerOption;
      const avatar = member.avatar ?? undefined;
      const subtitle = member.subtitle;
      return (
        // `gap-1.5` is §3.2's icon→label rule, the mirror of `SelectItem`'s
        // preserved `ItemText` recipe. This row was `gap-2` while
        // `AssigneeMenu`'s option rows — the other member picker in the app —
        // were `gap-1.5`, so the same list rendered 2px differently depending
        // on which wrapper opened it. The avatar stays 20px: these rows can
        // carry a subtitle, so they are two lines tall.
        <div className="flex w-full items-center gap-1.5">
          <Avatar size="sm" className="shrink-0">
            {avatar && <AvatarImage src={avatar} />}
            <AvatarFallback className="bg-hover-strong text-xs font-medium text-text-secondary">
              {initials(option.label)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-sm text-text">{option.label}</span>
              {member.isAgent ? <AgentBadge /> : null}
            </span>
            {subtitle && (
              <span className="truncate text-xs text-text-tertiary">
                {subtitle}
              </span>
            )}
          </div>
        </div>
      );
    }, []);

    return (
      <Picker
        ref={ref}
        options={options}
        value={value}
        onChange={onChange}
        multiple={multiple}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        className={className}
        renderOption={renderOption}
        renderValue={renderStack}
        {...rest}
      />
    );
  },
);
MemberCombobox.displayName = "MemberCombobox";

export { MemberCombobox };
